import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  ChangeDetectorRef,
} from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { FeatureService } from "src/app/services/features.service";
import { FeatureUpgradeService } from "src/app/services/feature-upgrade.service";
import { lastValueFrom, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { SideNavigatorService } from "src/app/services/side-navigator.service";
import { ScreenSizeService } from "src/app/services/screen-size.service";
import { AuthService } from "../../services/auth.service";
import { ApiUrlService } from "src/app/services/api-url.service";
import { HttpClient } from "@angular/common/http";
import { CacheService } from "src/app/services/cache.service";
import { NetworkService } from "src/app/services/network.service";
import { MessagingService } from "../../messaging.service";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class HomeComponent implements OnInit, OnDestroy {
  showScreen = false;
  features = [];
  orderedFeatures = [];
  firstName: string;
  organizationLogoUrl: string;

  isMobile: boolean;
  isDesktop: boolean;

  networkSubscription: Subscription | null = null;
  routeSubscription: Subscription | null = null;
  online = true;
  private hasInitialized = false;
  private isFetchingFeatures = false; // Prevent multiple simultaneous fetches
  hasFeatureOrder = false; // Track if we've loaded the feature order (or confirmed 404)

  isLoading: boolean = false; // Loading state - only true when downloading from web

  // Static map to store computed feature icon URLs - persists across navigation
  private static featureIconUrlMap: { [key: string]: string } = {};

  // Audio permission prompt
  showAudioPermissionPrompt: boolean = false;

  constructor(
    public sideNavigatorService: SideNavigatorService,
    private router: Router,
    private authService: AuthService,
    private featureService: FeatureService,
    private featureUpgradeService: FeatureUpgradeService,
    private screenSizeService: ScreenSizeService,
    private apiUrlService: ApiUrlService,
    private http: HttpClient,
    private cacheService: CacheService,
    private networkService: NetworkService,
    private cdr: ChangeDetectorRef,
    private messagingService: MessagingService
  ) {}

  async ngOnInit(): Promise<void> {
    // Listen for audio permission needed event
    window.addEventListener("AUDIO_PERMISSION_NEEDED", () => {
      console.log("Audio permission needed - showing prompt");
      this.showAudioPermissionPrompt = true;
      this.cdr.detectChanges();
    });

    try {
      const userInfo = await this.authService.getUserInfo(); // Await the async result of getUserInfo
      this.firstName = userInfo.firstname;
      // Use the active org, not the legacy field a switch never touches -
      // otherwise a multi-org user who's switched sees their original org's
      // logo instead of the one they're actually in.
      this.setOrganizationLogoUrl(
        userInfo.active_organization_id || userInfo.organization_id
      );
    } catch (error) {
      console.error("Error fetching user info:", error);
    }

    console.log("Network " + this.networkService.isOnline());
    this.online = this.networkService.isOnline();
    this.networkSubscription = this.networkService
      .getOnlineStatus()
      .subscribe((isOnline) => {
        console.log("Network status event:", isOnline);
        if (this.online !== isOnline) {
          console.log("Network status changed:", isOnline);
          this.online = isOnline;
          this.fetchAndOrderFeatures();
        }
      });

    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.screenSizeService.isDesktop$.subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    // Subscribe to router navigation events to refresh when returning to home
    this.routeSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        filter((event) => event.url === "/home")
      )
      .subscribe(() => {
        // Only refresh if we've already initialized (prevents double fetch on first load)
        if (this.hasInitialized) {
          console.log("Returning to home, refreshing features");
          this.fetchAndOrderFeatures();
        }
      });

    // Initial fetch
    this.fetchAndOrderFeatures();
    this.hasInitialized = true;
  }

  dismissAudioPrompt(): void {
    this.showAudioPermissionPrompt = false;
  }

  ngOnDestroy(): void {
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  navigateToFeature(feature: any): void {
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("🏠 Home: navigateToFeature called");
    console.log("🆔 featureId:", feature.feature_id);
    console.log("📂 defaultPath:", feature.defaultPath);
    console.log("🔗 defaultRoute:", feature.defaultRoute);
    console.log("🌐 Current URL:", this.router.url);

    // Set baseUrl in sessionStorage for the react-native-bundle component to use
    console.log(
      "💾 Home: Setting feature-base-path in sessionStorage:",
      feature.baseUrl
    );
    sessionStorage.setItem("feature-base-path", feature.baseUrl);

    // Navigate directly to the feature while preserving the home entry in history.
    console.log("[NAV] 🚀 Home: Navigating to new React Native route");
    console.log("[NAV] 📍 Home: Navigating to:", [
      "/react-native",
      feature.defaultPath.replace(/\//g, ""),
    ]);
    console.log("[NAV] 🔑 Home: With queryParams:", {
      navigationParam: feature.defaultRoute,
      uuid: feature.feature_id,
      source: "initial",
    });
    this.router.navigate(
      ["/react-native", feature.defaultPath.replace(/\//g, "")],
      {
        queryParams: {
          navigationParam: feature.defaultRoute,
          uuid: feature.feature_id,
          source: "initial",
        },
        replaceUrl: false,
      }
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  }

  onImageError() {
    this.networkService
      .checkInternetConnection()
      .then((isConnected) => {
        if (isConnected) {
          console.log("Internet is active");
        } else {
          console.log("Internet is down");
        }
      })
      .catch((error) =>
        console.error("Unexpected error in connectivity check:", error)
      );
  }

  filterFeaturesForScreenSize() {
    if (this.isDesktop) {
      return this.orderedFeatures;
    }
    return this.orderedFeatures.filter((feature) => !feature.desktopOnly);
  }

  setOrganizationLogoUrl(organizationId: string): void {
    const localStorageKey = `organizationLogo_${organizationId}`;
    const cachedLogo = localStorage.getItem(localStorageKey);
    if (cachedLogo) {
      this.organizationLogoUrl = cachedLogo;
    }

    const apiUrl = this.apiUrlService.getApiUrl();
    const logoUrl = `${apiUrl}/api/organizations/logo?organization_id=${organizationId}`;

    this.http.get(logoUrl).subscribe(
      (response: any) => {
        if (response && response.logo) {
          const logoDataUrl = `data:image/png;base64,${response.logo}`;
          this.organizationLogoUrl = logoDataUrl;

          if (cachedLogo !== logoDataUrl) {
            localStorage.setItem(localStorageKey, logoDataUrl);
          }
        } else if (!cachedLogo) {
          this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
        }
      },
      (error) => {
        if (!cachedLogo) {
          this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
        }
      }
    );
  }

  async fetchAndOrderFeatures(): Promise<void> {
    // Prevent multiple simultaneous calls
    if (this.isFetchingFeatures) {
      console.log("Already fetching features, skipping duplicate call");
      return;
    }

    this.isFetchingFeatures = true;
    // Reset hasFeatureOrder at start of each fetch to handle login redirect flow
    this.hasFeatureOrder = false;
    console.log("Fetching and ordering features");
    console.log("Initial isLoading state:", this.isLoading);

    // Track if we're downloading features (not from cache)
    let isDownloadingFeatures = false;

    try {
      console.log("About to call getFeatureDescriptions");
      const data = await this.featureService
        .getFeatureDescriptions((isLoading) => {
          console.log("Loading state callback invoked with:", isLoading);
          // Track if we're downloading, but don't set isLoading false yet
          // We need to wait for ordering to complete too
          isDownloadingFeatures = isLoading;
          if (isLoading) {
            this.isLoading = true;
            this.cdr.detectChanges();
          }
          // Don't set isLoading = false here - wait for ordering to complete
        })
        .catch((err) => {
          console.error("getFeatureDescriptions threw error:", err);
          throw err;
        }); // Await async operation to get feature descriptions
      console.log("Home Feature Descriptions:", data);

      // Show every feature regardless of offline support - previously this
      // dropped anything not marked offline:"true" while offline, which
      // made the whole home screen reshuffle/shrink rather than just
      // flagging what's unavailable. Keep them all and annotate the ones
      // that likely won't work without a connection so the template can
      // overlay a warning on just those tiles instead.
      const isOnline = this.networkService.isOnline();
      this.features = data.map((f) => ({
        ...f,
        offlineUnsupported: f.offline !== "true" && !isOnline,
      }));
      console.log("Feature Descriptions:", this.features);

      try {
        console.log("[FEATURE-ORDER] Step 1: Getting user info...");
        const userInfo = await this.authService.getUserInfo(); // Await the async result of getUserInfo
        // Active org, not the legacy field - a multi-org user who's
        // switched away from it would 401 (the backend scopes this
        // endpoint to the caller's active org) and get logged out by the
        // auth interceptor's blanket 401 handling.
        const activeOrgId =
          userInfo?.active_organization_id || userInfo?.organization_id;
        console.log(
          "[FEATURE-ORDER] Step 2: User info retrieved, org_id:",
          activeOrgId
        );

        let orderList;
        try {
          console.log(
            "[FEATURE-ORDER] Step 3: Fetching feature order for org:",
            activeOrgId
          );
          orderList = await this.featureService.getFeatureOrder(activeOrgId); // Fetch feature order (returns cached immediately if available, blocks on first load)
          console.log(
            "[FEATURE-ORDER] Step 4: Feature order received:",
            orderList
          );
        } catch (firstError: any) {
          // Log detailed error info for debugging
          console.warn("[FEATURE-ORDER] Step 3 ERROR - First attempt failed:", {
            error: firstError,
            status: firstError?.status,
            message: firstError?.message,
            errorType: typeof firstError,
          });

          // If we get an auth error, wait and retry once (handles login redirect race condition)
          if (firstError?.status === 401 || firstError?.status === 403) {
            console.warn(
              "[FEATURE-ORDER] Auth error on first attempt, waiting 500ms and retrying..."
            );
            await new Promise((resolve) => setTimeout(resolve, 500));
            orderList = await this.featureService.getFeatureOrder(activeOrgId);
          } else {
            // For other errors, rethrow
            throw firstError;
          }
        }

        console.log("[FEATURE-ORDER] Order List", orderList);
        console.log(
          "[FEATURE-ORDER] Feature IDs from features:",
          this.features.map((f) => f.feature_id)
        );

        // Create a map of feature_id to order for quick lookup
        const orderMap = new Map<string, number>();
        if (orderList && orderList["list"] && orderList["list"].length > 0) {
          console.log("[FEATURE-ORDER] Order list items:", orderList["list"]);
          orderList["list"].forEach((item) => {
            orderMap.set(item.feature_id, item.order);
          });
        } else if (orderList && orderList.notFound) {
          console.log(
            "[FEATURE-ORDER] No feature order exists yet (404) - will sort all features alphabetically"
          );
        } else {
          console.warn(
            "[FEATURE-ORDER] Order list is empty or missing 'list' property - will sort all features alphabetically"
          );
        }

        console.log("[FEATURE-ORDER] Map size:", orderMap.size);
        console.log("[FEATURE-ORDER] Entries:", Array.from(orderMap.entries()));

        // If no custom order exists, sort all features alphabetically
        if (orderMap.size === 0) {
          console.log(
            "[FEATURE-ORDER] No custom order found, sorting all features alphabetically"
          );
          this.orderedFeatures = [...this.features].sort((a, b) =>
            a.homeName.localeCompare(b.homeName)
          );
        } else {
          // Separate features into two arrays:
          // 1. Features present in orderMap
          // 2. Features not present in orderMap
          const orderedFeatures: any[] = [];
          const unorderedFeatures: any[] = [];

          this.features.forEach((feature, index) => {
            if (orderMap.has(feature.feature_id)) {
              console.log(
                `Feature ${feature.homeName} (${
                  feature.feature_id
                }) found in orderMap with order ${orderMap.get(
                  feature.feature_id
                )}`
              );
              orderedFeatures.push(feature);
            } else {
              console.log(
                `Feature ${feature.homeName} (${feature.feature_id}) NOT found in orderMap, will be sorted alphabetically`
              );
              unorderedFeatures.push(feature);
            }
          });

          // Sort orderedFeatures based on their specified order
          orderedFeatures.sort((a, b) => {
            return (
              (orderMap.get(a.feature_id) || 0) -
              (orderMap.get(b.feature_id) || 0)
            );
          });

          // Sort unorderedFeatures alphabetically by homeName
          unorderedFeatures.sort((a, b) =>
            a.homeName.localeCompare(b.homeName)
          );

          // Combine both arrays, with orderedFeatures first
          // IMPORTANT: Only set orderedFeatures after all async operations complete
          // This prevents UI from rendering before ordering is applied
          this.orderedFeatures = [...orderedFeatures, ...unorderedFeatures];

          console.log(
            "[FEATURE-ORDER] Unordered Features (alphabetical):",
            unorderedFeatures
          );
          console.log("[FEATURE-ORDER] Ordered Features:", orderedFeatures);
        }

        console.log(
          "[FEATURE-ORDER] Final orderedFeatures set with",
          this.orderedFeatures.length,
          "features"
        );

        // Mark that we have feature order after orderedFeatures is fully set
        // This ensures UI doesn't render until ordering is complete
        this.hasFeatureOrder = true;

        // Only show loading if we were actually downloading from network
        // If features were cached, don't show skeleton loader at all
        this.isLoading = isDownloadingFeatures;
        this.cdr.detectChanges();

        // Note: If feature order was cached, the background refresh in the service
        // will silently update the cache. On next navigation to home, the updated
        // order will be used automatically.
      } catch (error) {
        console.error(
          "[FEATURE-ORDER] OUTER CATCH - Failed to fetch feature order:",
          {
            error: error,
            status: (error as any)?.status,
            message: (error as any)?.message,
            errorType: typeof error,
          }
        );
        // Mark that we've attempted to load the order
        this.hasFeatureOrder = true;

        // Fallback: Sort all features alphabetically by homeName
        this.orderedFeatures = [...this.features].sort((a, b) =>
          a.homeName.localeCompare(b.homeName)
        );
        console.log(
          "[FEATURE-ORDER] Features sorted alphabetically due to order fetch failure."
        );

        this.isLoading = isDownloadingFeatures; // Only show loading if downloading
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error("Failed to fetch feature descriptions:", error);
      this.isLoading = false; // Stop loading on error
      this.cdr.detectChanges(); // Ensure UI updates
    } finally {
      this.isFetchingFeatures = false; // Always reset the flag
    }
  }

  // Asynchronously load and cache the icon URL for a feature
  async getFeatureIconUrl(feature: any): Promise<void> {
    const iconUrl = `${feature.baseUrl}${feature.homeIcon}`;
    let computedUrl: string = "";

    console.log("Home screen iconUrl", iconUrl);

    const img = await lastValueFrom(
      this.cacheService.getResource(iconUrl, true)
    );

    // now destructure into `data` and `content`
    const { data, contentType: content } = img;

    if (data) {
      if (typeof data === "string" && data.startsWith("data:image")) {
        computedUrl = data;
        // } else if (img instanceof Blob) {
        //   computedUrl = URL.createObjectURL(img);
      } else {
        computedUrl = data as string;
      }
    } else {
      computedUrl = "";
    }

    HomeComponent.featureIconUrlMap[iconUrl] = computedUrl;
  }

  // Helper method to get the icon URL from the map; triggers asynchronous loading if needed
  getIcon(feature: any): string {
    const iconUrl = `${feature.baseUrl}${feature.homeIcon}`;
    if (!HomeComponent.featureIconUrlMap[iconUrl]) {
      this.getFeatureIconUrl(feature);
      return "";
    }
    return HomeComponent.featureIconUrlMap[iconUrl];
  }
}
